/**
 * La ficha de un estudio, tal como la devuelve `GET /corpus/fuentes/:slug`.
 *
 * Los siete nombres son los de `HECHOS_TRANSPARENCIA` del API. Se repiten aquí y no se importan
 * porque la web no depende del API: son dos despliegues distintos, y `@athena/shared` guarda lo que
 * los dos **comprueban**, no la forma de cada ruta.
 */
export const HECHOS_TRANSPARENCIA = ['publica', 'financia', 'aQuienPregunto', 'cuantos', 'cuando', 'como', 'preguntaLiteral'] as const;

export type NombreHechoTransparencia = (typeof HECHOS_TRANSPARENCIA)[number];

/**
 * Los que la ficha enseña cuando se saben. «La pregunta literal» no (Iván, 2026-10-01): casi
 * siempre faltaba y a la lectora no le aporta. El dato sigue en la base.
 */
export const HECHOS_VISIBLES = HECHOS_TRANSPARENCIA.filter(n => n !== 'preguntaLiteral');

/**
 * Los que, si faltan, se dicen en una sola línea al final («No indica …»). Que falte cómo lo hizo o
 * a quién preguntó no se anuncia: lo que no se sabe no ocupa una fila cada uno (Iván, 2026-10-01).
 */
export const HECHOS_RELEVANTES: readonly NombreHechoTransparencia[] = ['publica', 'financia', 'cuantos', 'cuando'];

export interface HechoDeclarado {
  readonly declarado: boolean;
  readonly valor: string | null;
  readonly pagina: number | null;
  readonly cita: string | null;
  /** Si este hecho en concreto se ha comprobado contra el documento, declare algo o no. */
  readonly verificado: boolean;
  /**
   * ING-36, solo en `publica` y `financia`: `'logo'` si el nombre solo se ve en un logo de esa
   * página (sin frase literal). `'texto'` o sin campo: una cita literal, lo de siempre.
   */
  readonly origen?: 'texto' | 'logo';
}

/** Cuántos datos citables tiene el estudio y de dónde salen. La API lo manda; la ficha pública ya no lo enseña. */
export interface EstadisticaDeFicha {
  readonly vigentes: number;
  readonly porOrigen: { readonly textoConCita: number; readonly tabla: number; readonly grafico: number; readonly textoSinCita: number };
  readonly paginas: readonly number[];
}

/** Un hecho del editor (AUT-01): valor+URL, o `null` si no se pudo verificar abriendo una página real. */
export interface HechoDelEditor {
  readonly valor: string;
  readonly url: string;
}
export interface CitaDelEditor {
  readonly cita: string;
  readonly url: string;
}

export interface Ficha {
  readonly fuente: {
    readonly slug: string;
    readonly tituloOriginal: string;
    readonly editor: string;
    readonly anio: number | null;
    readonly pais: string | null;
    readonly urlDocumento: string | null;
    readonly portadaUrl?: string | null;
    readonly anadidoEn?: string | null;
    /** Si Athena ha mirado la transparencia de este estudio. Sin mirar, que falte un hecho no dice nada. */
    readonly transparenciaRevisada: boolean;
    readonly transparencia: Partial<Record<NombreHechoTransparencia, HechoDeclarado>>;
    /**
     * Quién es el editor (AUT-01), no lo que declara el estudio: los seis hechos de transparencia
     * (arriba) son del documento. Esto es de la organización que lo publica.
     */
    readonly editorAQueSeDedica: CitaDelEditor | null;
    readonly editorPais: HechoDelEditor | null;
    readonly editorWeb: HechoDelEditor | null;
    /**
     * ING-36: quién lo publica y quién lo financia según su web o la página del informe, nunca el
     * documento. Opcionales: una API anterior no los trae.
     */
    readonly publicaSegunWeb?: HechoDelEditor | null;
    readonly financiaSegunWeb?: HechoDelEditor | null;
  };
  readonly estadistica: EstadisticaDeFicha;
}

/**
 * Qué enseña la ficha para un hecho de transparencia.
 *
 * **«El estudio no lo dice» solo si alguien lo ha mirado.** Hasta el 2026-09-23 la ficha lo decía
 * siempre que faltaba el hecho, y 136 de las 144 fuentes no tenían la transparencia revisada: Athena
 * afirmaba de un estudio algo que nadie había comprobado (revisión de Astra, F4 y F5).
 *
 * **Por hecho, no por fuente entera.** Hasta el 2026-09-29 "revisado" era un único booleano de toda
 * la fuente, así que una fuente con verificación parcial (algunos de los 7 hechos comprobados, otros
 * no: el caso normal del pipeline por lotes) o salía toda "pendiente" o toda "revisada". Ahora cada
 * hecho trae su propio `verificado`.
 */
export function estadoDelHecho(hecho: HechoDeclarado | undefined): 'declarado' | 'noDeclarado' | 'pendiente' {
  if (hecho?.declarado === true && hecho.valor !== null) return 'declarado';
  return hecho?.verificado === true ? 'noDeclarado' : 'pendiente';
}

/**
 * El idioma de una cita literal del editor (AUT-01), para el atributo `lang` de la web: sin él, un
 * lector de pantalla lee en español una cita en árabe o en alemán, que es peor que no marcar nada.
 *
 * Heurística por script y por palabras frecuentes, igual de aproximada que `idiomaDeLaCita` de
 * `ListaFuentes.tsx` (que mira el slug del estudio, no el texto): aquí sí hay el texto en sí, así
 * que se mira directamente. No hay un campo de idioma guardado en la ficha del editor.
 */
export function idiomaDeLaCitaDelEditor(texto: string): string | undefined {
  if (/[؀-ۿ]/u.test(texto)) return 'ar';
  if (/[一-鿿]/u.test(texto)) return 'zh';
  if (/[Ѐ-ӿ]/u.test(texto)) return 'ru';
  if (/\b(und|für|wir|unsere|ist)\b/iu.test(texto)) return 'de';
  if (/\b(nous|des|notre|et|pour)\b/iu.test(texto)) return 'fr';
  if (/\b(somos|nuestro|para|que)\b/iu.test(texto)) return 'es';
  return 'en';
}

/** Cómo se sostiene una fila de la ficha: una cita con su página, un logo en una página o su web. */
export type Respaldo =
  | { readonly tipo: 'cita'; readonly cita: string | null; readonly pagina: number | null }
  | { readonly tipo: 'logo'; readonly pagina: number | null }
  | { readonly tipo: 'web'; readonly url: string };

export interface PresentacionTransparencia {
  /** Solo lo que se sabe, en el orden de siempre. */
  readonly filas: readonly { readonly nombre: NombreHechoTransparencia; readonly valor: string; readonly respaldo: Respaldo }[];
  /** Comprobado que no indica patrocinador: quién lo publica, para decirlo en la misma línea. */
  readonly sinPatrocinadorLoPublica: string | null;
  /** Lo relevante comprobado que no indica (sin el patrocinador, que va en su propia frase). */
  readonly noIndica: readonly NombreHechoTransparencia[];
  /** Lo relevante que nadie ha mirado todavía: no se afirma que no lo diga. */
  readonly sinRevisar: readonly NombreHechoTransparencia[];
}

/**
 * Qué enseña la ficha de la transparencia (Iván, 2026-10-01): solo lo que se sabe, una fila cada
 * cosa; lo relevante que falta, en una línea al final.
 *
 * Quién publica y quién financia se saben si los dice el documento (texto o logo) o, si no, su web
 * (ING-36). Si no hay patrocinador, nunca un «no lo dice» a secas: se dice quién lo publica, lo
 * primero que haya de documento, web o catálogo.
 */
export function presentarTransparencia(fuente: Ficha['fuente']): PresentacionTransparencia {
  const segunWeb = (n: NombreHechoTransparencia): HechoDelEditor | null =>
    n === 'publica' ? (fuente.publicaSegunWeb ?? null) : n === 'financia' ? (fuente.financiaSegunWeb ?? null) : null;

  const filas: PresentacionTransparencia['filas'][number][] = [];
  for (const nombre of HECHOS_VISIBLES) {
    const hecho = fuente.transparencia[nombre];
    if (hecho !== undefined && estadoDelHecho(hecho) === 'declarado' && hecho.valor !== null) {
      const respaldo: Respaldo =
        hecho.origen === 'logo' ? { tipo: 'logo', pagina: hecho.pagina } : { tipo: 'cita', cita: hecho.cita, pagina: hecho.pagina };
      filas.push({ nombre, valor: hecho.valor, respaldo });
      continue;
    }
    const web = segunWeb(nombre);
    if (web !== null) filas.push({ nombre, valor: web.valor, respaldo: { tipo: 'web', url: web.url } });
  }

  const sabido = new Set(filas.map(f => f.nombre));
  const faltan = (estado: 'noDeclarado' | 'pendiente') =>
    HECHOS_RELEVANTES.filter(n => !sabido.has(n) && estadoDelHecho(fuente.transparencia[n]) === estado);

  const noIndica = faltan('noDeclarado');
  const quienPublica = filas.find(f => f.nombre === 'publica')?.valor ?? (fuente.editor.trim() === '' ? null : fuente.editor);
  const sinPatrocinador = noIndica.includes('financia') && quienPublica !== null;
  return {
    filas,
    sinPatrocinadorLoPublica: sinPatrocinador ? quienPublica : null,
    noIndica: sinPatrocinador ? noIndica.filter(n => n !== 'financia') : noIndica,
    sinRevisar: faltan('pendiente'),
  };
}

/** Una lista en prosa: «a, b y c» en español, «a, b and c» en inglés. */
export function enProsa(partes: readonly string[], idioma: 'es' | 'en', tipo: 'conjunction' | 'disjunction' = 'conjunction'): string {
  // «No indica a, b ni c»: en español la negación se une con «ni», que Intl no tiene.
  if (idioma === 'es' && tipo === 'disjunction' && partes.length > 1) return `${partes.slice(0, -1).join(', ')} ni ${partes.at(-1) ?? ''}`;
  return new Intl.ListFormat(idioma, { type: tipo }).format(partes);
}
